import React, {ChangeEvent, ReactNode, useEffect, useState} from 'react';
import {Caption, MediaAnnotationSource, Tag} from '../../reducers/media';
import {useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {RootState, useAppDispatch} from '../../store';
import {getFileMetaData} from '../../actions/media';
import {apiPost, get} from '../../utils/requests';
import {SWFT_DIGITAL_API_HOST} from '../../constants';
import {toast} from 'react-toastify';
import {CaptionIcon, CrossIcon, MagicStick, TranslateIcon} from '../../ui-components/Iconpack';
import {CommonSpinLoader} from '../../ui-components/loaders';
import Tracker from 'src/lib/analytics/tracker';
import {ErrorTags, EventNames, EventProperty, ScreenNames} from '../../lib/analytics/events';
import Feedback from '../../screens/Transformation/components/Feedback';
import {Select, SelectContent, SelectItem, SelectTrigger} from '../../ui-components/Select';
import {SupportedLanguages} from './LanguageMap';

export const MediaAnnotationsSubMenu = () => {
    const dispatch = useAppDispatch();
    const [caption, setCaption] = useState<string | null>(null);
    const [originalCaption, setOriginalCaption] = useState<string | null>(null);
    const [tags, setTags] = useState<string[]>([]);
    const [originalTags, setOriginalTags] = useState<string[]>([]);
    const [captionLoading, setCaptionLoading] = useState<boolean>(false);
    const [tagsLoading, setTagsLoading] = useState<boolean>(false);
    const [captionEdited, setCaptionEdited] = useState<boolean>(false);
    const [tagsEdited, setTagsEdited] = useState<boolean>(false);
    const [newTag, setNewTag] = useState<string>('');
    const [generateCaptionUsed, setGenerateCaptionUsed] = useState<boolean>(false);
    const [generateTagsUsed, setGenerateTagsUsed] = useState<boolean>(false);
    const [captionTranslationLanguage, setCaptionTranslationLanguage] = useState<string>();
    const [tagsTranslationLanguage, setTagsTranslationLanguage] = useState<string>();

    const {fileUid: media_uid} = useParams();
    const [mediaProps, userProfileProps] = [
        useSelector((state: RootState) => state.media),
        useSelector((state: RootState) => state.userProfile),
        useSelector((state: RootState) => state.settings),
    ];

    const org_uid = userProfileProps.currentOrganization.uid;

    const actualCaptionText =
        mediaProps.fileData &&
        mediaProps.fileData.caption &&
        mediaProps.fileData.caption.source_type === MediaAnnotationSource.USER
            ? mediaProps.fileData.caption.text
            : null;

    const actualTagsTextList = mediaProps.fileData.tags
        .filter((tag) => tag.source_type === MediaAnnotationSource.USER)
        .map((tag) => tag.text);

    const commonEventProps = {
        [EventProperty.ScreenName]: ScreenNames.TRANSFORMATION_SCREEN,
        [EventProperty.Email]: userProfileProps.email,
        [EventProperty.FirstName]: userProfileProps.first_name,
        [EventProperty.FileUid]: media_uid,
    };

    function trackError(e: any, id: string, props: Object) {
        Tracker.trackError(e, ErrorTags.MEDIA_ANNOTATIONS_ERROR, {
            ...commonEventProps,
            [EventProperty.ID]: id,
            ...props,
        });
    }

    function trackEvent(id: string, props: Object) {
        Tracker.trackEvent(EventNames.MEDIA_ANNOTATIONS, {
            ...commonEventProps,
            [EventProperty.ID]: id,
            ...props,
        });
    }

    async function updateMediaProps() {
        dispatch(getFileMetaData({orgUid: org_uid, fileUid: media_uid!!}));
    }

    const handleCaptionChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setGenerateCaptionUsed(false);
        setCaption(event.target.value);
        setOriginalCaption(event.target.value);
        setCaptionEdited(true);
    };

    const handleRemoveTag = (tag: string) => {
        setGenerateTagsUsed(false);
        setTags(tags.filter((t) => t !== tag));
        setOriginalTags(tags.filter((t) => t !== tag));
        setTagsEdited(true);
    };

    const handleAddTag = (tag: string) => {
        setGenerateTagsUsed(false);
        if (!tags.includes(tag)) {
            setTags([...tags, tag]);
            setOriginalTags([...tags, tag]);
            setTagsEdited(true);
        }
        setNewTag('');
    };

    const handleGenerateTags = async () => {
        setGenerateTagsUsed(true);
        trackEvent('generate_tags', {});
        setTagsLoading(true);
        const body = {
            org_uid: org_uid,
            media_uid: media_uid,
            action: 'generate_tags',
        };
        const res = await apiPost<any>(SWFT_DIGITAL_API_HOST + '/media/annotations/', body);
        setTagsLoading(false);
        const generated_tags: Tag[] = res.data && res.data['tags'] ? res.data['tags'] : [];
        if (res.error || generated_tags.length === 0) {
            trackError(res.error, 'generate_tags_error', {message: res.error});
            toast.error(res.error ?? 'Something went wrong, we could not generate tags for the current image.');
            return;
        }

        // tags generated successfully
        const tags_text_list = generated_tags
            .map((tag) => tag.text)
            .filter((value, index, self) => self.indexOf(value) === index);
        console.log(tags_text_list);
        setTags(tags_text_list);
        setOriginalTags(tags_text_list);
        setTagsEdited(true);
    };

    const handleTagsSave = async () => {
        if (tags !== actualTagsTextList) {
            trackEvent('save_tags', {});
            setTagsLoading(true);
            const body = {
                org_uid: org_uid,
                media_uid: media_uid,
                tags: tags.map((tag) => tag.trim()),
                action: 'update',
            };
            const res = await apiPost<any>(SWFT_DIGITAL_API_HOST + '/media/annotations/', body);
            setTagsLoading(false);
            if (res.error) {
                trackError(res.error, 'save_tags_error', {message: res.error});
                toast.error('Something went wrong while saving tags. Please try again later.');
            }
            await updateMediaProps();
        }
        setTagsEdited(false);
    };

    const handleGenerateCaption = async () => {
        setGenerateCaptionUsed(true);
        trackEvent('generate_caption', {});

        setCaptionLoading(true);
        const body = {
            org_uid: org_uid,
            media_uid: media_uid,
            action: 'generate_caption',
        };
        const res = await apiPost<any>(SWFT_DIGITAL_API_HOST + '/media/annotations/', body);
        setCaptionLoading(false);
        const generate_captions: Caption[] = res.data && res.data['captions'] ? res.data['captions'] : [];
        if (res.error || generate_captions.length === 0) {
            trackError(res.error, 'generate_caption_error', {message: res.error});
            toast.error(res.error ?? `something went wrong, please try again`);
            return;
        }

        // caption generated successfully
        const best_caption = generate_captions.sort((a, b) => b.score - a.score)[0];
        setCaption(best_caption.text);
        setOriginalCaption(best_caption.text);
        setCaptionEdited(true);
    };

    const handleCaptionSave = async () => {
        trackEvent('save_caption', {});

        if (captionEdited && caption !== actualCaptionText) {
            setCaptionLoading(true);
            const body = {
                org_uid: org_uid,
                media_uid: media_uid,
                captions: [caption],
                action: 'update',
            };
            const res = await apiPost<any>(SWFT_DIGITAL_API_HOST + '/media/annotations/', body);
            setCaptionLoading(false);
            if (res.error) {
                trackError(res.error, 'save_caption_error', {message: res.error});
                toast.error('Something went wrong while saving caption. Please try again later.');
            }
            await updateMediaProps();
        }
        setCaptionEdited(false);
    };

    useEffect(() => {
        if (mediaProps.fileData && mediaProps.fileData.caption) {
            setCaption(actualCaptionText);
            setOriginalCaption(actualCaptionText);
        }

        if (mediaProps.fileData && mediaProps.fileData.tags.length > 0) {
            setTags(actualTagsTextList);
            setOriginalTags(actualTagsTextList);
        }
    }, [mediaProps.fileData]);

    useEffect(() => {
        if (mediaProps.fileData && mediaProps.fileData.caption) {
            setCaption(actualCaptionText);
            setOriginalCaption(actualCaptionText);
        }

        if (mediaProps.fileData && mediaProps.fileData.tags.length > 0) {
            setTags(actualTagsTextList);
            setOriginalTags(actualTagsTextList);
        }
    }, []);

    const Btn = (props: {onClick: () => void; children?: ReactNode}) => {
        return (
            <div
                className='border-1 text-inter flex cursor-pointer justify-center rounded-2xl bg-[#FFF] px-2 py-3 text-[14px] font-[700] text-[#1570EF]'
                onClick={props.onClick}>
                {props.children}
            </div>
        );
    };

    const AnnotationGenerateBtn = (props: {text: string; onGenerate: () => void}) => {
        return (
            <Btn onClick={props.onGenerate}>
                <span className={'px-3'}>{props.text}</span>
                <MagicStick />
            </Btn>
        );
    };

    useEffect(() => {
        if (captionTranslationLanguage) {
            setCaptionLoading(true);
            get(
                `https://gentle-journey-39823-d0423db9dfd4.herokuapp.com/translate?text=${originalCaption}&lang=${captionTranslationLanguage}`,
            )
                .then((res) => setCaption(res['translated_text']))
                .catch((err) => toast.error('Something went wrong while translating the caption. Please retry.'))
                .finally(() => setCaptionLoading(false));
        }
    }, [captionTranslationLanguage]);

    useEffect(() => {
        if (tagsTranslationLanguage) {
            interface Translation {
                original_text: string;
                translated_text: string;
            }

            interface TranslationResponse {
                status: number;
                destination_language: string;
                translations: Translation[];
            }
            setTagsLoading(true);
            const translationRequest = {
                texts: originalTags,
                lang: tagsTranslationLanguage,
            };
            apiPost<TranslationResponse>(
                'https://gentle-journey-39823-d0423db9dfd4.herokuapp.com/translate',
                translationRequest,
            )
                .then((res) => {
                    if (res.data) {
                        const translatedTags = res.data.translations.map((text) => text.translated_text);
                        setTags(translatedTags);
                    }
                })
                .catch((err) => toast.error('Something went wrong while translating the tags. Please retry.'))
                .finally(() => setTagsLoading(false));
        }
    }, [tagsTranslationLanguage]);

    return (
        <div className={'flex flex-col gap-10'}>
            <div>
                <div className='flex items-center'>
                    <div className='block cursor-pointer items-center justify-center rounded-[6px] border-[2px] border-[#4E5BA6] bg-[#717BBC] p-[12px] md:mx-[16px] md:hidden'>
                        <CaptionIcon color='#FFFFFF' />
                    </div>
                    <div className='flex w-full items-center justify-between pl-[6px] text-[14px] font-[700] text-[#FFFFFF] md:pl-0'>
                        <span>CAPTION</span>
                        {caption && (
                            <div className='flex items-center'>
                                <TranslateIcon />
                                <span className='text-[12px] text-white'>Translate to</span>
                                <Select
                                    onValueChange={(value) => {
                                        setCaptionTranslationLanguage(value);
                                    }}>
                                    <SelectTrigger
                                        noStyle={true}
                                        width={16}
                                        height={16}
                                        color={'#FFFFFF'}></SelectTrigger>
                                    <SelectContent className='outline-none'>
                                        {Object.keys(SupportedLanguages).map((language) => (
                                            <SelectItem key={language} value={language}>
                                                {SupportedLanguages[language as keyof typeof SupportedLanguages]}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>
                </div>
                {/*captions*/}
                <div className='my-4 rounded-2xl bg-[#363F72] p-2'>
                    <div className='flex items-center justify-center'>
                        {captionLoading ? (
                            <CommonSpinLoader className={'h-[95px]'} size={'sm'} />
                        ) : (
                            <textarea
                                className='text-inter w-full resize-none rounded border-none bg-[#363F72] px-2 py-1 text-[14px] text-[#FFF] outline-none'
                                placeholder={'enter caption...'}
                                value={caption ? caption : ''}
                                onChange={handleCaptionChange}
                                rows={4}
                            />
                        )}
                    </div>
                </div>
                {/*feedback*/}
                {caption && generateCaptionUsed && (
                    <div>
                        <Feedback
                            feedbackText={'Did you like the generated caption ?'}
                            identifier={'CAPTION_GEN'}
                            file_uid={''}
                            onFeedbackClick={() => {
                                setGenerateCaptionUsed(false);
                            }}
                        />
                    </div>
                )}
                {/*save caption*/}
                {caption && captionEdited && (
                    <Btn onClick={handleCaptionSave}>
                        <span className={'px-3 text-[14px] font-[700]'}>Save</span>
                    </Btn>
                )}
                {/*generate captions*/}
                {!caption && <AnnotationGenerateBtn text={'Generate Caption'} onGenerate={handleGenerateCaption} />}
            </div>

            {/*tags*/}

            <div>
                <div className='flex w-full items-center justify-between pl-[6px] text-[14px] font-[700] text-[#FFFFFF] md:pl-0'>
                    <span>TAGS</span>
                    {tags && tags.length > 0 && (
                        <div className='flex items-center'>
                            <TranslateIcon />
                            <span className='text-[12px] text-white'>Translate to</span>
                            <Select
                                onValueChange={(value) => {
                                    setTagsTranslationLanguage(value);
                                }}>
                                <SelectTrigger noStyle={true} width={16} height={16} color={'#FFFFFF'}></SelectTrigger>
                                <SelectContent className='outline-none'>
                                    {Object.keys(SupportedLanguages).map((language) => (
                                        <SelectItem key={language} value={language}>
                                            {SupportedLanguages[language as keyof typeof SupportedLanguages]}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </div>{' '}
                <div className='my-4 rounded-2xl bg-[#363F72] p-2'>
                    <div className='flex items-center justify-center'>
                        {tagsLoading ? (
                            <CommonSpinLoader className={'h-[95px]'} size={'sm'} />
                        ) : (
                            <div className='flex max-h-[400px] flex-wrap gap-1 overflow-y-auto rounded-[16px] bg-[#363F72] p-1'>
                                {tags.map((tag, index) => (
                                    <div
                                        key={index}
                                        className='flex items-center rounded-[12px] bg-[#293056] p-2 font-[400] text-white'>
                                        <span className='mr-1'>{tag}</span>
                                        <button onClick={() => handleRemoveTag(tag)} className='text-white'>
                                            <CrossIcon />
                                        </button>
                                    </div>
                                ))}
                                <div className='flex items-center'>
                                    <input
                                        type='text'
                                        className='text-inter overflow-y-auto rounded border-none bg-[#363F72] px-2 py-1 text-[14px] text-[#FFF] outline-none'
                                        placeholder='+ new tag'
                                        value={newTag}
                                        onChange={(e) => setNewTag(e.target.value.trim() || '')}
                                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleAddTag(newTag)}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {/*feedback*/}
                {tags.length > 0 && generateTagsUsed && (
                    <div>
                        <Feedback
                            feedbackText={'Did you like the generated tags ?'}
                            identifier={'TAGS_GEN'}
                            file_uid={''}
                            onFeedbackClick={() => {
                                setGenerateTagsUsed(false);
                            }}
                        />
                    </div>
                )}
                {/*save tags*/}
                {tags.length > 0 && tagsEdited && (
                    <Btn onClick={handleTagsSave}>
                        <span className={'px-3 text-[14px] font-[700]'}>Save</span>
                    </Btn>
                )}
                {/*generate tags*/}
                {tags.length === 0 && <AnnotationGenerateBtn text={'Generate Tags'} onGenerate={handleGenerateTags} />}
            </div>
        </div>
    );
};
